import { useRef, useState, useEffect } from 'react'
import s from './HtmlEditor.scss'
import {
  PrintPage,
  HtmlToComponent,
  PricingTable,
  PricingTableSimple_V1,
  PricingTableSimple_V2
} from 'components/ideal-core-ui'
import { Rnd } from 'react-rnd'
import { FaPencilAlt, FaPlusCircle, FaTrashAlt } from 'react-icons/fa'
import { Sidebar } from './Sidebar'
import cn from 'classnames'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { DragTarget } from './DragTarget'
import { AddBlockModal } from 'components/common/modals/AddBlockModal'
import { useImmer } from 'use-immer'
import noop from 'lodash/noop'
import { getCorrectDroppedOffsetValue } from 'utils/helpers'
import update from 'immutability-helper'

const initialBlock = {
  name: '',
  category: '',
  visible: 'me',
  type: 'block',
  content: ''
}

const initialPages = [
  {
    content: []
  }
]

const HtmlEditor = ({ pageType = 'letter', pages: pagesProps, categories, actions }) => {
  const htmlTemplate = useRef(null)
  const pagesRef = useRef([])
  const [isAddBlockModalVisible, setIsAddBlockModalVisible] = useState(false)
  const [block, setBlock] = useImmer(initialBlock)
  const [blockToEditIndexes, setBlockToEditIndexes] = useState(null)
  const [pages, setPages] = useImmer(initialPages)

  useEffect(() => {
    setPages(pagesProps)
  }, [pagesProps, setPages])

  const removePage = index => {
    const array = [...pages]
    array.splice(index, 1)
    setPages(() => array)
  }

  const addPage = pageIndex => {
    const array = [...pages]
    array.splice(pageIndex + 1, 0, { content: [] })
    setPages(() => array)
  }

  const duplicatePage = pageIndex => {
    const array = [...pages]
    const duplicatedPage = { ...array[pageIndex] }
    array.splice(pageIndex + 1, 0, duplicatedPage)
    setPages(() => array)
  }

  const reorderPage = (pageIndex, direction) => {
    const array = [...pages]
    const page = array[pageIndex]
    array.splice(pageIndex, 1)
    array.splice(pageIndex + direction, 0, page)
    setPages(() => array)
  }

  const addBlockToPage = (pageIndex, block, monitor, ref) => {
    const section = categories[block.category].find(s => s.id === block.id)
    setPages(draft => {
      draft[pageIndex].content.push({
        ...section,
        position: getCorrectDroppedOffsetValue(
          ref,
          monitor.getInitialSourceClientOffset(),
          monitor.getSourceClientOffset()
        )
      })
    })
  }

  const setBlockSize = (pageIndex, sectionIndex, block, size) => {
    setPages(draft => {
      draft[pageIndex].content[sectionIndex].size = size
    })
  }

  const setBlockPosition = (pageIndex, sectionIndex, block, position) => {
    setPages(draft => {
      draft[pageIndex].content[sectionIndex].position = position
    })
  }

  const removeBlockFromPage = (pageIndex, blockIndex) => {
    const newState = update(pages, {
      [pageIndex]: {
        content: { $splice: [[blockIndex, 1]] }
      }
    })
    setPages(newState)
  }

  const editBlockFromPage = (newContent, blockToEditIndexes) => {
    const { pageIndex, sectionIndex } = blockToEditIndexes
    const newState = update(pages, {
      [pageIndex]: {
        content: {
          [sectionIndex]: {
            content: { $set: newContent }
          }
        }
      }
    })
    setPages(newState)
  }

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <div className={s.container}>
          <div className={s.scrollable}>
            <div className={s[pageType]} ref={htmlTemplate}>
              {pages.map((page, pageIndex) => (
                <div
                  className={s.page}
                  key={pageIndex}
                  ref={el => (pagesRef.current[pageIndex] = el)}
                  style={{
                    minHeight: pageType === 'a4' ? '297mm' : '279.4mm',
                    width: pageType === 'a4' ? '210mm' : '215.9mm',
                    position: 'relative',
                    lineHeight: 1
                  }}
                >
                  <DragTarget
                    accept="Block"
                    onDrop={(block, monitor) =>
                      addBlockToPage(pageIndex, block, monitor, pagesRef.current[pageIndex])
                    }
                  >
                    <PrintPage
                      type={pageType}
                      removePage={pages.length > 1 ? () => removePage(pageIndex) : null}
                      duplicatePage={() => duplicatePage(pageIndex)}
                      isFirstPage={pageIndex === 0}
                      isLastPage={pageIndex === pages.length - 1}
                      reorderPage={direction => reorderPage(pageIndex, direction)}
                    >
                      {page.content.map((section, sectionIndex) => {
                        return (
                          <Rnd
                            style={{ overflow: 'hidden' }}
                            key={`${pageIndex}-${sectionIndex}-${section.id}`}
                            bounds="parent"
                            className={s.rnd}
                            size={section.size}
                            position={section.position}
                            onDragStop={(e, d) => {
                              setBlockPosition(pageIndex, sectionIndex, section, { x: d.x, y: d.y })
                            }}
                            onResizeStop={(e, direction, ref, delta, position) => {
                              setBlockSize(pageIndex, sectionIndex, section, {
                                width: ref.style.width,
                                height: ref.style.height
                              })
                              setBlockPosition(pageIndex, sectionIndex, section, position)
                            }}
                          >
                            {section.type === 'PRICING_TABLE' ? (
                              (() => {
                                switch (section.subType) {
                                  case 'SIMPLE_PRICING_TABLE_V1':
                                    return (
                                      <PricingTableSimple_V1
                                        isPdf
                                        content={section.content}
                                        icons={
                                          <div className={cn('remove-for-pdf-js', s.icons)}>
                                            <FaPencilAlt
                                              onClick={() => {
                                                setIsAddBlockModalVisible(true)
                                                setBlock(() => section)
                                                setBlockToEditIndexes({
                                                  pageIndex,
                                                  sectionIndex
                                                })
                                              }}
                                              key="0"
                                            />
                                            <FaTrashAlt
                                              onClick={() =>
                                                removeBlockFromPage(pageIndex, sectionIndex)
                                              }
                                              key="1"
                                            />
                                          </div>
                                        }
                                      />
                                    )
                                  case 'SIMPLE_PRICING_TABLE_V2':
                                    return (
                                      <PricingTableSimple_V2
                                        isPdf
                                        content={section.content}
                                        icons={
                                          <div className={cn('remove-for-pdf-js', s.icons)}>
                                            <FaPencilAlt
                                              onClick={() => {
                                                setIsAddBlockModalVisible(true)
                                                setBlock(() => section)
                                                setBlockToEditIndexes({
                                                  pageIndex,
                                                  sectionIndex
                                                })
                                              }}
                                              key="0"
                                            />
                                            <FaTrashAlt
                                              onClick={() =>
                                                removeBlockFromPage(pageIndex, sectionIndex)
                                              }
                                              key="1"
                                            />
                                          </div>
                                        }
                                      />
                                    )
                                  default:
                                    return (
                                      <PricingTable
                                        isPdf
                                        content={section.content}
                                        icons={
                                          <div className={cn('remove-for-pdf-js', s.icons)}>
                                            <FaPencilAlt
                                              onClick={() => {
                                                setIsAddBlockModalVisible(true)
                                                setBlock(() => section)
                                                setBlockToEditIndexes({
                                                  pageIndex,
                                                  sectionIndex
                                                })
                                              }}
                                              key="0"
                                            />
                                            <FaTrashAlt
                                              onClick={() =>
                                                removeBlockFromPage(pageIndex, sectionIndex)
                                              }
                                              key="1"
                                            />
                                          </div>
                                        }
                                      />
                                    )
                                }
                              })()
                            ) : (
                              <HtmlToComponent
                                html={section.content}
                                icons={
                                  <div className={cn('remove-for-pdf-js', s.icons)}>
                                    <FaPencilAlt
                                      onClick={() => {
                                        setIsAddBlockModalVisible(true)
                                        setBlock(() => section)
                                        setBlockToEditIndexes({
                                          pageIndex,
                                          sectionIndex
                                        })
                                      }}
                                      key="0"
                                    />
                                    <FaTrashAlt
                                      onClick={() => removeBlockFromPage(pageIndex, sectionIndex)}
                                      key="1"
                                    />
                                  </div>
                                }
                              />
                            )}
                          </Rnd>
                        )
                      })}
                    </PrintPage>
                  </DragTarget>
                  {addPage && (
                    <p
                      className={cn('remove-for-pdf-js', s.addPage)}
                      onClick={() => addPage(pageIndex)}
                    >
                      <FaPlusCircle /> Add Page
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
          <Sidebar
            actions={actions}
            pageType={pageType}
            categories={categories}
            htmlTemplate={htmlTemplate}
            pages={pages}
          />
        </div>
      </DndProvider>
      {isAddBlockModalVisible && (
        <AddBlockModal
          fromTemplate
          isAddBlockModalVisible={isAddBlockModalVisible}
          setIsAddBlockModalVisible={setIsAddBlockModalVisible}
          saveBlock={(content, destroyEditor) => {
            editBlockFromPage(content, blockToEditIndexes)
            destroyEditor && destroyEditor()
            setIsAddBlockModalVisible(false)
          }}
          setBlock={setBlock}
          block={block}
          resetBlock={noop}
        />
      )}
    </>
  )
}

export default HtmlEditor
